import constants from '../constants'

/**
 * Market-specific configuration for the ns-city-state-zip form component.
 * This configuration takes the form of a map of key-value pairs, where the key is the two-digit country code and the value
 * is a {@link CityStateZipConfigEntry} object containing that country's configuration settings as properties.
 * @type {Object<string, CityStateZipConfigEntry>}
 */
export const CITY_STATE_ZIP_CONFIG = {
    "AR": {
        "displayOrder": [["zip"],["city"],["state"]]
    },
    "AT": {
        "displayOrder": [["city"],["zip"]]
    },
    "AU": {
        "displayOrder": [["city"],["state"],["zip"]]
    },
    "BE": {
        "displayOrder": [["city"],["zip"]]
    },
    "BN": {
        "displayOrder": [["zip"],["city"],["state"]],
        "enableZipCodeAutocomplete": true,
        "cityDropdown": true
    },
    "BR": {
        "displayOrder": [["city"],["state"],["zip"]],
        "optionalState": true
    },
    "CA": {
        "displayOrder": [["city"],["state"],["zip"]]
    },
    "CH": {
        "displayOrder": [["city"],["zip"]]
    },
    "CL": {
        "displayOrder": [["zip"],["city"],["county"],["state"]],
        "optionalZip": true
    },
    "CO": {
        "displayOrder": [["city"],["state"]],
    },
    "CR": {
        "displayOrder": [["city"],["state"]],
        "zip": "99999"
    },
    "CZ": {
        "displayOrder": [["city"],["zip"]]
    },
    "DE": {
        "displayOrder": [["city"],["zip"]]
    },
    "DK": {
        "displayOrder": [["city"],["zip"]]
    },
    "ES": {
        "displayOrder": [["city"],["zip"]]
    },
    "FI": {
        "displayOrder": [["city"],["zip"]]
    },
    "FR": {
        "displayOrder": [["city"],["zip"]]
    },
    "GB": {
        "displayOrder": [["city"],["county"],["zip"]],
        "optionalCounty": true
    },
    "GT": {
        "displayOrder": [["city"],["state"],["zip"]],
        "optionalZip": true
    },
    "HK": {
        "displayOrder": [["city"]],
        "state": "HK"
    },
    "HN": {
        "displayOrder": [["city"],["state"],["zip"]],
        "optionalZip": true
    },
    "HU": {
        "displayOrder": [["city"],["zip"]]
    },
    "ID": {
        "displayOrder": [["zip"],["city"],["state"]],
        "enableZipCodeAutocomplete": true,
        "cityDropdown": true
    },
    "IE": {
        "displayOrder": [["city"],["zip"]],
        "optionalZip": true
    },
    "IL": {
        "displayOrder": [["city"],["zip"]],
        "state": "IL"
    },
    "IS": {
        "displayOrder": [["city"],["zip"]]
    },
    "IT": {
        "displayOrder": [["city"],["zip"]]
    },
    "JP": {
        "cityRegex": constants.JP.regex.allowedCharacters,
        "displayOrder": [["zip"],["state"],["city"],["county"]],
        "enableZipCodeAutocomplete": true,
        "optionalCounty":  true,
        "countyPattern": constants.JP.regex.allowedCharacters,
        "disableCountyDropdown": true,
        "resetOnNullZipSearch": true
    },
    "LU": {
        "displayOrder": [["city"],["zip"]]
    },
    "MX": {
        "displayOrder": [["zip", "state"],["city"],["county"]],
        "enableZipCodeAutocomplete": true,
        "countyDropdown": true,
        "stateDropdown": false,
        "cityDropdown": true
    },
    "MY": {
        "displayOrder": [["zip"],["city"],["state"]],
        "enableZipCodeAutocomplete": true,
        "cityDropdown": true
    },
    "NC": {
        "displayOrder": [["city"],["county"],["zip"],["country"]],
        "optionalZip": true
    },
    "NL": {
        "displayOrder": [["city"],["zip"],["country"]]
    },
    "NO": {
        "displayOrder": [["city"],["zip"]]
    },
    "NZ": {
        "displayOrder": [["city"],["county"],["state"],["zip"]],
        "optionalCounty": true,
        "optionalState": true
    },
    "PE": {
        "displayOrder": [["state","county"],["city","zip"]],
        "populateCountiesFromState": true,
        "cityDropdown": true,
        "populateCitiesFromCounty": true
    },
    "PF": {
        "displayOrder": [["city"],["county"],["zip"],["country"]]
    },
    "PH": {
        "displayOrder": [["city","zip"]],
        "state": "PH"
    },
    "PL": {
        "displayOrder": [["city"],["zip"]],
        "state": "PL"
    },
    "PT": {
        "displayOrder": [["city"],["zip"]]
    },
    "RO": {
        "displayOrder": [["city"],["zip"]]
    },
    "RU": {
        "displayOrder": [["city"],["state"],["zip"]],
        "optionalState": true
    },
    "SE": {
        "displayOrder": [["city"],["zip"]]
    },
    "SG": {
        "displayOrder": [["zip"]],
        "city": "Singapore",
        "state": "SG"
    },
    "SK": {
        "displayOrder": [["city"],["zip"]]
    },
    "SV": {
        "displayOrder": [["city"],["state"],["zip"]],
        "optionalZip": true
    },
    "TH": {
        "displayOrder": [["city"],["zip"]]
    },
    "TR": {
        "displayOrder": [["city"],["zip"]]
    },
    "UA": {
        "displayOrder": [["city"],["state"],["zip"]],
        "optionalState": true
    },
    "US": {
        "displayOrder": [["city"],["state"],["zip"]]
    },
    "VE": {
        "displayOrder": [["city"],["state"],["zip"]]
    },
    "VN": {
        "displayOrder": [["state","county"],["city"]],
        "useVNRegionListApproach": true,
        "cityDropdown": true
    },
    "ZA": {
        "displayOrder": [['county'], ["city"], ["zip"], ["state"]]
    }
};

/**
 * Configuration for the City, County, State, and Zip Code form fields for a single market.
 * Most of the configuration settings represented in this object are optional, and only need to be specified if
 * the market differs from the defaults given here.
 * @typedef CityStateZipConfigEntry
 * @property {string[][]} displayOrder - (REQUIRED) Form field display order. This is a nested array of strings controlling the
 * order in which form fields are displayed. The outer level of the array determines the order of rows. The inner level
 * of the array determines the order of fields within a row. Strings are form field keys: "city", "county", "state", "zip".
 * Form field keys omitted from this array are omitted from the form.
 * @property {boolean} [enableZipCodeAutocomplete = false] - (optional) Flag indicating that postal code lookup should be used to
 * populate the City, County, and State fields. `false` by default.
 * @property {boolean} [cityDropdown = false] - (optional) Flag indicating that the city field is a dropdown. This is intended
 * for use with Zip Code Autocomplete. `false` by default.
 * @property {boolean} [countyDropdown = false] - (optional) Flag indicating that the county field is a dropdown. This is intended 
 * for use with Zip Code Autocomplete. `false` by default.
 * @property {boolean} [stateDropdown = true] - (optional) Flag indicating that the state field is a dropdown. `true` by default.
 * @property {boolean} [populateCountiesFromState = false] - (optional) Flag indicating that the County dropdown field
 * should be populated from the user's State selection. `false` by default.
 * @property {boolean} [useVNRegionListApproach] - (optional) Flag indicating whether to use the VN region list approach.
 * @property {boolean} [optionalCity = false] - (optional) Flag indicating that the City field is not required.
 * `false` by default (i.e. City is required). Applicable only if City field is present.
 * @property {boolean} [optionalCounty = false] - (optional) Flag indicating that the County field is not required.
 * `false` by default (i.e. County is required by default). Applicable only if County field is present.
 * @property {boolean} [optionalState = false] - (optional) Flag indicating that the State field is not required.
 * `false` by default (i.e. State is required by default). Applicable only if State field is present.
 * @property {boolean} [optionalZip = false] - (optional) Flag indicating that the Zip Code field is not required.
 * `false` by default (i.e. Zip Code is required by default). Applicable only if Zip Code field is present.
 * @property {string} [city] - (optional) Initial value for the City field. This also serves as a hardcoded value if the
 * City field is not displayed in the form.
 * @property {string} [county] - (optional) Initial value for the County field. This also serves as a hardcoded value if
 * the County field is not displayed in the form.
 * @property {string} [state] - (optional) Initial value for the State field. This also serves as a hardcoded value if
 * the State field is not displayed in the form.
 * @property {string} [zip] - (optional) Initial value for the Zip Code field. This also serves as a hardcoded value if
 * the Zip Code field is not displayed in the form.
 */
